import Decimal from 'decimal.js';

import { HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment } from '@/modules/summaryPanels/HypotheticalTransfersSummaryPanel/graphql/HypotheticalTransfersSummary.generated';
import { EstateWaterfallTransferKind } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import {
  getTransferDestinationName,
  getTransferSourceName,
} from '@/utils/hypotheticalTransfers/transferUtils';

import {
  GetHypotheticalTransfers_EstateWaterfallLoanFragment,
  GetHypotheticalTransfers_EstateWaterfallTransferFragment,
  GetHypotheticalTransfers_HypotheticalGratFragment,
  GetHypotheticalTransfers_HypotheticalTransferFragment,
} from './graphql/GetHypotheticalTransfers.generated';
import {
  SaleLoanWithProjections,
  TransferWithValue,
} from './hooks/useGetHypotheticalTransfers';
import { TransferTab } from './TransferItems';

export function getTransferFrequency({
  startYear,
  endYear,
}: {
  startYear?: number | undefined | null;
  endYear?: number | undefined | null;
}) {
  if (startYear && endYear && endYear !== startYear) {
    return 'Recurring';
  }

  return 'One-time';
}

/**
 * Get the display name for a transfer, including a fallback based on the start and end years
 */
export function getTransferDisplayName({
  name,
  startYear,
  endYear,
}: {
  name?: string | undefined | null;
  startYear?: number | undefined | null;
  endYear?: number | undefined | null;
}): string {
  if (name) return name;

  return `${getTransferFrequency({
    startYear,
    endYear,
  })} transfer`;
}

export function getDescriptionFromTransfer(
  transfer: GetHypotheticalTransfers_HypotheticalTransferFragment,
  kind: TransferTab
) {
  if (kind === TransferTab.Inbound) {
    return `From ${getTransferSourceName(transfer)}`;
  }

  return `To ${getTransferDestinationName(transfer)}`;
}

function getNetHypotheticalSaleLoans({
  recipientLoans,
  sourceLoans,
}: {
  recipientLoans: SaleLoanWithProjections[];
  sourceLoans: SaleLoanWithProjections[];
}) {
  const lastSaleLoanYear = Math.max(
    ...recipientLoans.map(
      ({ saleLoan }) =>
        saleLoan.startDate.getFullYear() + saleLoan.termLengthYears
    ),
    ...sourceLoans.map(
      ({ saleLoan }) =>
        saleLoan.startDate.getFullYear() + saleLoan.termLengthYears
    )
  );

  const inboundAmount = recipientLoans.reduce((acc, { loanProjections }) => {
    const value = loanProjections
      ? getSaleLoanFundingValueFromEWLoan(loanProjections)
      : new Decimal(0);
    return acc.plus(value);
  }, new Decimal(0));

  const outboundAmount = sourceLoans.reduce((acc, { loanProjections }) => {
    const value = loanProjections
      ? getSaleLoanFundingValueFromEWLoan(loanProjections)
      : new Decimal(0);
    return acc.plus(value);
  }, new Decimal(0));

  const netTransferred = inboundAmount.minus(outboundAmount);
  return {
    lastSaleLoanYear,
    totalSaleLoanValue: netTransferred,
  };
}

function getNetHypotheticalGratAnnuities({
  recipientGrats,
  sourceGrats,
  transferLog,
  inboundGratAnnuities,
  outboundGratAnnuities,
}: {
  recipientGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  sourceGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[];
  inboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
}) {
  const associatedGratAnnuities = inboundGratAnnuities.concat(
    outboundGratAnnuities
  );

  const lastGratAnnuityYear = Math.max(
    ...associatedGratAnnuities.map((t) => t.year)
  );

  const inboundAnnuityValue = sumDecimalJS(
    inboundGratAnnuities.map(
      (annuity) => annuity.finalTransferAmount ?? new Decimal(0)
    )
  );

  const outboundAnnuityValue = sumDecimalJS(
    outboundGratAnnuities.map(
      (annuity) => annuity.finalTransferAmount ?? new Decimal(0)
    )
  );

  const inboundFundingValue = recipientGrats.reduce(
    (acc, { simulatedGrat }) => {
      const value = getHypotheticalGratFundingValueFromTransferLog(
        simulatedGrat.id,
        transferLog
      );
      return acc.plus(value);
    },
    new Decimal(0)
  );

  const outboundFundingValue = sourceGrats.reduce((acc, { simulatedGrat }) => {
    const value = getHypotheticalGratFundingValueFromTransferLog(
      simulatedGrat.id,
      transferLog
    );
    return acc.plus(value);
  }, new Decimal(0));

  const netTransferred = inboundAnnuityValue
    .minus(outboundAnnuityValue)
    .plus(inboundFundingValue)
    .minus(outboundFundingValue);

  return {
    lastGratAnnuityYear,
    totalGratAnnuityValue: netTransferred,
  };
}

function getNetHypotheticalBeneficiaryPayments({
  inboundBeneficiaryPayments,
  outboundBeneficiaryPayments,
}: {
  inboundBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  outboundBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
}) {
  const inboundTotalValue = sumDecimalJS(
    inboundBeneficiaryPayments.map(
      ({ finalTransferAmount }) => finalTransferAmount ?? new Decimal(0)
    )
  );

  const outboundTotalValue = sumDecimalJS(
    outboundBeneficiaryPayments.map(
      ({ finalTransferAmount }) => finalTransferAmount ?? new Decimal(0)
    )
  );

  const netTransferred = inboundTotalValue.minus(outboundTotalValue);

  const lastBeneficiaryPaymentYear = Math.max(
    ...inboundBeneficiaryPayments.map(({ year }) => year),
    ...outboundBeneficiaryPayments.map(({ year }) => year)
  );

  return {
    lastBeneficiaryPaymentYear,
    totalBeneficiaryPaymentValue: netTransferred,
  };
}

/**
 * Get the initial funding value of a sale or loan
 */
export function getSaleLoanFundingValueFromEWLoan(
  loan: GetHypotheticalTransfers_EstateWaterfallLoanFragment
): Decimal {
  // the beginningOfYearValue in the first year of the loan is the funding value of
  // the sale or loan. it's better to use this computed value than to attempt to reimplement
  // all of the logic that computes the value quickly and correctly on the backend
  return loan.valueHistory[0]?.beginningOfYearValue ?? new Decimal(0);
}

/**
 * Get the initial funding value of a hypothetical GRAT
 */
export function getHypotheticalGratFundingValueFromTransferLog(
  gratEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): Decimal {
  return (
    transferLog.find(
      (t) =>
        t.destination?.id === gratEntityId &&
        t.kind === EstateWaterfallTransferKind.HypotheticalGratFunding
    )?.finalTransferAmount ?? new Decimal(0)
  );
}

/**
 * Get the initial funding value of a hypothetical CRT
 */
export function getHypotheticalCrtFundingValueFromTransferLog(
  crtEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): Decimal {
  return (
    transferLog.find(
      (t) =>
        t.destination?.id === crtEntityId &&
        t.kind === EstateWaterfallTransferKind.HypotheticalCrtFunding
    )?.finalTransferAmount ?? new Decimal(0)
  );
}

/**
 * Get the initial funding value of a hypothetical CLT
 */
export function getHypotheticalCltFundingValueFromTransferLog(
  cltEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): Decimal {
  return (
    transferLog.find(
      (t) =>
        t.destination?.id === cltEntityId &&
        t.kind === EstateWaterfallTransferKind.HypotheticalCltFunding
    )?.finalTransferAmount ?? new Decimal(0)
  );
}

/**
 * Get the year from a hypothetical GRAT funding transfer log item
 */
export function getHypotheticalGratFundingYearFromTransferLog(
  gratEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): number | undefined {
  return transferLog.find(
    (t) =>
      t.destination?.id === gratEntityId &&
      t.kind === EstateWaterfallTransferKind.HypotheticalGratFunding
  )?.year;
}

/**
 * Get the year from a hypothetical CRT funding transfer log item
 */
export function getHypotheticalCrtFundingYearFromTransferLog(
  crtEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): number | undefined {
  return transferLog.find(
    (t) =>
      t.destination?.id === crtEntityId &&
      t.kind === EstateWaterfallTransferKind.HypotheticalCrtFunding
  )?.year;
}

/**
 * Get the year from a hypothetical CLT funding transfer log item
 */
export function getHypotheticalCltFundingYearFromTransferLog(
  cltEntityId: string,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
): number | undefined {
  return transferLog.find(
    (t) =>
      t.destination?.id === cltEntityId &&
      t.kind === EstateWaterfallTransferKind.HypotheticalCltFunding
  )?.year;
}

/**
 * Get the total value of all repayments of a sale or loan
 */
export function getSaleLoanRepaymentValueFromEWLoan(
  loan: GetHypotheticalTransfers_EstateWaterfallLoanFragment
): Decimal {
  return loan.valueHistory.reduce((acc, yearValue) => {
    const principalPaid = yearValue.principalPaid ?? new Decimal(0);
    const interestPaid = yearValue.interestPaid ?? new Decimal(0);
    return acc.plus(principalPaid).plus(interestPaid);
  }, new Decimal(0));
}

interface GetNetHypotheticalTransfersArg {
  /**
   * All inbound transfers
   */
  inboundTransfers: TransferWithValue[];
  /**
   * All outbound transfers
   */
  outboundTransfers: TransferWithValue[];
  /**
   * All sale loans that the entity/person is the recipient of
   */
  recipientLoans: SaleLoanWithProjections[];
  /**
   * All sale loans that the entity/person is the source of
   */
  sourceLoans: SaleLoanWithProjections[];
  /**
   * All GRATs that the entity/person is the recipient of
   */
  recipientGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  /**
   * All GRATs that the entity/person is the source of
   */
  sourceGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  /**
   * The transfer log
   */
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[];
  /**
   * All GRAT annuities that the entity/person is the source of
   */
  inboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  /**
   * All GRAT annuities that the entity/person is the recipient of
   */
  outboundGratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  /**
   * All beneficiary payments that the entity/person is the source of
   */
  outboundBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  /**
   * All beneficiary payments that the entity/person is the recipient of
   */
  inboundBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
}

export function getNetHypotheticalTransfers({
  inboundTransfers,
  outboundTransfers,
  recipientLoans,
  sourceLoans,
  recipientGrats,
  sourceGrats,
  transferLog,
  outboundGratAnnuities,
  inboundGratAnnuities,
  outboundBeneficiaryPayments,
  inboundBeneficiaryPayments,
}: GetNetHypotheticalTransfersArg): {
  totalNormalizedTransfers: Decimal;
  finalYear: number;
} {
  const allTransfers = [...inboundTransfers, ...outboundTransfers];

  const currentYear = new Date().getFullYear();
  // get the last transfer year
  let lastTransferYear = currentYear;

  if (allTransfers.length) {
    lastTransferYear = Math.max(
      ...allTransfers.map((t) => t.transfer.endYear ?? currentYear)
    );
  }

  const { lastSaleLoanYear, totalSaleLoanValue } = getNetHypotheticalSaleLoans({
    recipientLoans,
    sourceLoans,
  });

  if (lastSaleLoanYear > lastTransferYear) {
    lastTransferYear = lastSaleLoanYear;
  }

  const { lastGratAnnuityYear, totalGratAnnuityValue } =
    getNetHypotheticalGratAnnuities({
      recipientGrats,
      sourceGrats,
      transferLog,
      inboundGratAnnuities,
      outboundGratAnnuities,
    });

  if (lastGratAnnuityYear > lastTransferYear) {
    lastTransferYear = lastGratAnnuityYear;
  }

  const { lastBeneficiaryPaymentYear, totalBeneficiaryPaymentValue } =
    getNetHypotheticalBeneficiaryPayments({
      outboundBeneficiaryPayments,
      inboundBeneficiaryPayments,
    });

  if (lastBeneficiaryPaymentYear > lastTransferYear) {
    lastTransferYear = lastBeneficiaryPaymentYear;
  }

  const inboundTransfersAmount = sumDecimalJS(
    inboundTransfers.map((t) => t.value)
  );
  const outboundTransfersAmount = sumDecimalJS(
    outboundTransfers.map((t) => t.value)
  );
  const totalNormalizedTransfers = inboundTransfersAmount
    .minus(outboundTransfersAmount)
    .plus(totalSaleLoanValue)
    .plus(totalGratAnnuityValue)
    .plus(totalBeneficiaryPaymentValue);

  return {
    totalNormalizedTransfers,
    finalYear: lastTransferYear,
  };
}
